'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface SuccessStory {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  story: string;
  achievement: string;
  beforeEarnings: string;
  afterEarnings: string;
  timeframe: string;
  category: string;
}

const successStories: SuccessStory[] = [
{
  id: 1,
  name: "Kavita Menon",
  role: "Romance Storyteller",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d71d6fc6-1763295655575.png",
  alt: "Indian woman with shoulder-length hair wearing white shirt smiling confidently at camera",
  story: "I started as a homemaker with a passion for storytelling. Within 6 months on StoryNest-FM, I built an audience of 500K followers and now earn more than my previous corporate job. The platform gave me the freedom to work from home while pursuing my passion.",
  achievement: "Built 500K+ followers in 6 months",
  beforeEarnings: "₹0",
  afterEarnings: "₹4.5L/month",
  timeframe: "6 months",
  category: "Romance"
},
{
  id: 2,
  name: "Rahul Verma",
  role: "Tech Podcaster",
  image: "https://images.unsplash.com/photo-1666833110817-c180a2ba4d61",
  alt: "Young Indian man wearing glasses and casual shirt sitting at desk with laptop and recording equipment",
  story: "As a software engineer, I wanted to share tech knowledge in Hindi. StoryNest-FM's creator tools made it easy to start. Now I reach 1M+ listeners monthly and collaborate with major tech brands for sponsored content. It's my full-time career now.",
  achievement: "1M+ monthly listeners",
  beforeEarnings: "₹0",
  afterEarnings: "₹6.8L/month",
  timeframe: "8 months",
  category: "Technology"
},
{
  id: 3,
  name: "Deepa Krishnan",
  role: "Mythology Narrator",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
  alt: "Indian woman with long black hair wearing traditional jewelry and red saree smiling warmly",
  story: "I've been telling mythological stories for years at local events. StoryNest-FM helped me reach a national audience. My Tamil narrations now have 800K followers, and I've published 3 audio books through the platform. The cultural impact has been incredible.",
  achievement: "Published 3 audio books",
  beforeEarnings: "₹15K/month",
  afterEarnings: "₹5.2L/month",
  timeframe: "10 months",
  category: "Mythology"
}];


export default function SuccessStoriesSection() {
  const [activeStory, setActiveStory] = useState(0);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary/10 rounded-full mb-4">
            <Icon name="TrophyIcon" size={20} className="text-secondary" />
            <span className="text-sm font-semibold text-secondary">Success Stories</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Real Creators, Real Success
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Hear from creators who transformed their passion into thriving careers
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-warm-lg overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="relative h-96 lg:h-auto">
              <AppImage
                src={successStories[activeStory].image}
                alt={successStories[activeStory].alt}
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent" />
              
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-warm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                      <Icon name="CheckCircleIcon" size={24} className="text-success-foreground" />
                    </div>
                    <div>
                      <div className="font-bold text-text-primary">{successStories[activeStory].achievement}</div>
                      <div className="text-sm text-text-secondary">in {successStories[activeStory].timeframe}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div>
                      <div className="text-xs text-text-secondary">Before</div>
                      <div className="text-lg font-bold text-text-primary">{successStories[activeStory].beforeEarnings}</div>
                    </div>
                    <Icon name="ArrowRightIcon" size={20} className="text-primary" />
                    <div className="text-right">
                      <div className="text-xs text-text-secondary">After</div>
                      <div className="text-lg font-bold text-success">{successStories[activeStory].afterEarnings}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="mb-6">
                <div className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
                  {successStories[activeStory].category}
                </div>
                <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                  {successStories[activeStory].name}
                </h3>
                <p className="text-text-secondary">{successStories[activeStory].role}</p>
              </div>

              <div className="mb-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <p className="text-text-secondary leading-relaxed italic">
                    "{successStories[activeStory].story}"
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 mb-8">
                {successStories.map((story, index) =>
                <button
                  key={story.id}
                  onClick={() => setActiveStory(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeStory ? 'w-12 bg-primary' : 'w-2 bg-border hover:bg-primary/50'}`
                  }
                  aria-label={`View story ${index + 1}`} />

                )}
              </div>

              <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary-dark hover:shadow-warm">
                <Icon name="PlayIcon" size={20} />
                <span>Watch Full Interview</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          <div className="bg-card rounded-xl p-6 text-center shadow-warm">
            <div className="text-3xl font-heading font-bold text-primary mb-2">95%</div>
            <div className="text-sm text-text-secondary">Creator Satisfaction Rate</div>
          </div>
          <div className="bg-card rounded-xl p-6 text-center shadow-warm">
            <div className="text-3xl font-heading font-bold text-success mb-2">₹50L+</div>
            <div className="text-sm text-text-secondary">Total Paid to Creators</div>
          </div>
          <div className="bg-card rounded-xl p-6 text-center shadow-warm">
            <div className="text-3xl font-heading font-bold text-accent mb-2">10K+</div>
            <div className="text-sm text-text-secondary">Active Creators</div>
          </div>
        </div>
      </div>
    </section>);

}